import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { Flex, Box } from '@osmosis/tailwind-ui/components/Box';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { faAngleDown } from '@fortawesome/pro-solid-svg-icons/faAngleDown';
import { faAngleUp } from '@fortawesome/pro-solid-svg-icons/faAngleUp';
import { AnimatePresence } from 'motion/react';
import {
  TrackBreadcrumbHandler,
  BreadcrumbV2Object,
} from '@osmosis/shared/types/learn';
import BreadcrumbsV2 from './BreadcrumbsV2';
import { text5 } from '@osmosis/tailwind-ui/styles/text';
import MotionDiv from '@osmosis/nextjs-components/MotionDiv';

type Props = {
  breadcrumb: BreadcrumbV2Object[];
  sectionName?: string;
  justifyDropdown?: 'flex-start' | 'center' | 'flex-end';
  onBreadCrumbItemClick?: TrackBreadcrumbHandler;
  linkClassName?: string;
  dropdownButtonClassName?: string;
  target?: string;
  dropdownTitleOverride?: string;
};

/**
 * Renders dropdown for V2 breadcrumbs.
 */
const BreadcrumbsDropdownV2 = (props: Props) => {
  const {
    breadcrumb,
    sectionName,
    justifyDropdown = 'center',
    onBreadCrumbItemClick,
    linkClassName = '',
    dropdownButtonClassName = '',
    target = '_self',
    dropdownTitleOverride,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const { formatMessage } = useIntl();

  return (
    <section
      aria-label={
        sectionName
          ? formatMessage(
              {
                id: 'learn.breadcrumbs.section',
                defaultMessage: 'Breadcrumbs for "{sectionName}"',
              },
              { sectionName },
            )
          : undefined
      }
    >
      <Box>
        <Flex
          className={`justify-${justifyDropdown} items-center`}
          data-testid="breadcrumbs-v2-dropdown"
        >
          <Button
            onClick={() => setIsOpen(!isOpen)}
            data-testid="Also-appears-in-wrapper"
            aria-expanded={isOpen}
            className={`items-center, border-none, bg-transparent font-normal hover:bg-transparent active:bg-transparent ${dropdownButtonClassName}`}
          >
            <P
              data-testid="breadcrumbs-dropdown-button"
              className={`py-2 text-left !text-xs ${text5} opacity-75`}
            >
              {dropdownTitleOverride ? (
                dropdownTitleOverride
              ) : (
                <FormattedMessage
                  id="learn.breadcrumb.also_appears"
                  defaultMessage="Also appears in"
                />
              )}
            </P>
            <Icon
              icon={isOpen ? faAngleUp : faAngleDown}
              className="ml-1 text-sm text-slate-600 dark:text-slate-300"
            />
          </Button>
        </Flex>

        <AnimatePresence initial={false}>
          <MotionDiv
            transition={{ duration: 0.5 }}
            initial={{
              height: isOpen ? 0 : 'auto',
              opacity: isOpen ? 0 : 1,
            }}
            animate={{
              height: isOpen ? 'auto' : 0,
              opacity: isOpen ? 1 : 0,
            }}
            exit={{
              height: isOpen ? 'auto' : 0,
              opacity: isOpen ? 1 : 0,
            }}
          >
            {isOpen && (
              <Flex className="flex-col">
                {breadcrumb.map((item) => (
                  <Box
                    key={item.link}
                    className="mb-2"
                    data-testid={'breadcrumbs-v2-dropdown-content'}
                  >
                    <BreadcrumbsV2
                      breadcrumb={item}
                      linkClassName={linkClassName}
                      target={target}
                      onBreadCrumbItemClick={onBreadCrumbItemClick}
                    />
                  </Box>
                ))}
              </Flex>
            )}
          </MotionDiv>
        </AnimatePresence>
      </Box>
    </section>
  );
};

export default BreadcrumbsDropdownV2;
